import { format, parseISO } from 'date-fns';
import { Building2, CheckCircle2, User } from 'lucide-react';
import { Text } from '@musa.tech/design-system';
import type { MeasurementApproval } from '@/integrations/faturamento/client';

function formatMoment(value: string): string {
  try {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm');
  } catch {
    return value;
  }
}

/**
 * Como a aprovação é apresentada, conforme quem aprovou.
 *
 * São dois perfis, e a diferença importa para quem lê: o próprio cliente encerrando o prazo, ou
 * um colaborador da Musa antecipando-o. O nome sozinho não diz de qual lado veio.
 */
function describeApprover(approval: MeasurementApproval): {
  title: string;
  subtitle?: string;
  fromMusa: boolean;
} {
  const fromMusa = approval.realm === 'musa';
  const name = approval.name?.trim();
  const email = approval.email?.trim();
  const who = name || email;

  return {
    // Sem nome nem e-mail — token de service account, ou claim ausente — o rastro que resta é o
    // lado de quem aprovou. Melhor do que um campo vazio, e a data continua valendo.
    title: who
      ? `Aprovada por ${who}${fromMusa ? ' (Musa)' : ''}`
      : fromMusa
        ? 'Aprovada por um colaborador da Musa'
        : 'Aprovada por você',
    subtitle: name && email ? email : undefined,
    fromMusa,
  };
}

interface MeasurementHistoryProps {
  approval?: MeasurementApproval | null;
}

/**
 * Histórico da medição — hoje, só a aprovação.
 *
 * A seção **não aparece** sem aprovação, que é o caso da liberação automática: prazo vencido,
 * medição sem janela de retenção, ou contestação resolvida. Ali não houve aprovação de ninguém, e
 * um card dizendo "aprovada" com a data da rotina atribuiria a alguém uma decisão que não foi
 * tomada.
 *
 * Independente do histórico de contestações, que continua aparecendo pelas próprias regras: uma
 * medição pode ter as duas seções, uma só, ou nenhuma.
 */
export function MeasurementHistory({ approval }: MeasurementHistoryProps) {
  if (!approval) return null;

  const approver = describeApprover(approval);

  return (
    <section className="space-y-2">
      <Text variant="heading" size="large" className="text-neutral-900">
        Histórico da medição
      </Text>

      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                approver.fromMusa ? 'bg-primary-50' : 'bg-success-100'
              }`}
            >
              {approver.fromMusa ? (
                <Building2 className="h-3.5 w-3.5 text-primary-700" />
              ) : (
                <User className="h-3.5 w-3.5 text-success-700" />
              )}
            </span>
            <div className="flex flex-col">
              <Text variant="label" size="small" className="text-neutral-900">
                {approver.title}
              </Text>
              {approver.subtitle && (
                <Text variant="body" size="small" className="text-neutral-500">
                  {approver.subtitle}
                </Text>
              )}
            </div>
          </div>
          <Text variant="body" size="small" className="text-neutral-500">
            {formatMoment(approval.approved_at)}
          </Text>
        </div>

        <div className="mt-2 flex items-start gap-1.5 rounded-md bg-neutral-50 p-3">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success-700" />
          <Text variant="body" size="small" className="text-neutral-800">
            A medição foi aprovada e liberada para faturamento antes do fim do prazo.
          </Text>
        </div>
      </div>
    </section>
  );
}
